import React, { useMemo, useState, useEffect } from 'react';
import keyBy from 'lodash/keyBy';

import { MarketGroupOverviewModel } from '@om-fe/models';
import { PlatformTypes } from '@om-fe/utils';
import { GetMarketGroupsResponse, GetMarketGroupsOptions } from '@om-fe/api';
import { RpcStateStatus, useRpcState } from '@om-fe/widgets/dist/common/rpc/rpc-state.hooks';

import { MatchListCommon, FilteredMarketGroupsOverview } from '../../utils/match-list-models';
import { MatchListItemLayout } from '../../utils/match-list.constants';
import { MatchOfTheDayMatchListPublicProps } from './match-of-the-day-match-list';
import { useI18NContext } from '@om-fe/core/core/i18n/useI18ncontext';
import MatchListSkeleton from '../../match-list-skeleton';
import MatchOfTheDayMarketGroups from './match-of-the-day-market-groups';

export interface MatchOfTheDayMatchListSelectorPublicProps extends MatchListCommon {
    readonly instanceId: string;
    readonly disciplineId: string;
    readonly itemLayout?: MatchListItemLayout;
    readonly marketGroupsOverviewNumber?: number;
    readonly deviceType?: PlatformTypes;
    readonly showMarketSelectorHeader?: boolean;
    readonly locationId?: string;
    readonly eventCategoryId?: string;
    readonly preventRedirect?: boolean;
    readonly noRedirectFilter?: (genericId?: string) => void;
    readonly genericId?: string;
    readonly dateRange: string;
}

type MatchOfTheDayMatchListSelectorProps = MatchOfTheDayMatchListSelectorPublicProps
    & MatchOfTheDayMatchListPublicProps;

const MatchOfTheDayMatchListSelector: React.FC<MatchOfTheDayMatchListSelectorProps> = (
    {
        instanceId,
        disciplineId,
        liveStatus,
        limitNumberOfEvents,
        mainMarketsLimit,
        marketsHeaderOptions,
        itemLayout,
        showMarketSelectorHeader,
        locationId,
        eventCategoryId,
        preventRedirect,
        noRedirectFilter,
        genericId,
        dateRange,
    }: MatchOfTheDayMatchListSelectorProps,
) => {
    const { gettext } = useI18NContext();
    const [matchOfTheDayGroupOverviewData, setMatchOfTheDayGroupOverviewData] = useState<string | undefined>(undefined);
    const [filteredMarketGroupsOverview, setFilteredMarketGroupsOverview] = useState<Record<string, FilteredMarketGroupsOverview>>();
    const [filteredMarketGroupsOverviewList, setFilteredMarketGroupsOverviewList] = useState<string[]>([]);
    const params: GetMarketGroupsOptions = useMemo(() => ({ sportId: disciplineId, liveStatus }), [disciplineId, liveStatus]);
    const [rpcResponse] = useRpcState<GetMarketGroupsResponse>('marketGroupsOverview', params);

    useEffect(() => {
        if (rpcResponse.status === RpcStateStatus.Success) {
            const { data } = rpcResponse;
            const marketGroupsOverviewData: MarketGroupOverviewModel[] | undefined = data.response;
            const filteredMarketGroupsOverviewData: Record<string, FilteredMarketGroupsOverview> = keyBy(marketGroupsOverviewData, 'id');
            const filteredMarketGroupsOverviewListData = marketGroupsOverviewData.map(item => item.id);
            const matchOfTheDayGroupOverviewData = filteredMarketGroupsOverviewListData.slice(0, mainMarketsLimit).join(',');
            setFilteredMarketGroupsOverview(filteredMarketGroupsOverviewData);
            setFilteredMarketGroupsOverviewList(filteredMarketGroupsOverviewListData);
            setMatchOfTheDayGroupOverviewData(matchOfTheDayGroupOverviewData);
        } else if (rpcResponse.status === RpcStateStatus.Failed) {
            setMatchOfTheDayGroupOverviewData('');
        } else if(rpcResponse.status === RpcStateStatus.Loading){
            setMatchOfTheDayGroupOverviewData(undefined);
        }
    }, [rpcResponse, mainMarketsLimit]);

    if (
        rpcResponse.status === RpcStateStatus.Loading ||
        rpcResponse.status === RpcStateStatus.Initial ||
        rpcResponse.status === RpcStateStatus.Failed ||
        typeof matchOfTheDayGroupOverviewData === 'undefined'
    ) {
        return <MatchListSkeleton />;
    }

    if (matchOfTheDayGroupOverviewData === '') {
        return (
                <div className="EmptyList">
                    {gettext('No matches currently available')}
                </div>
            );
    }

    return (
        <>
            <MatchOfTheDayMarketGroups
                disciplineId={disciplineId}
                instanceId={instanceId}
                liveStatus={liveStatus}
                limitNumberOfEvents={limitNumberOfEvents}
                marketsHeaderOptions={marketsHeaderOptions}
                mainMarketsLimit={mainMarketsLimit}
                groupOverview={matchOfTheDayGroupOverviewData}
                hasMarketSelector={true}
                itemLayout={itemLayout}
                locationId={locationId}
                eventCategoryId={eventCategoryId}
                preventRedirect={preventRedirect}
                noRedirectFilter={noRedirectFilter}
                genericId={genericId}
                dateRange={dateRange} 
                limit={limitNumberOfEvents!}
                filteredMarketGroupsOverview={filteredMarketGroupsOverview}
                filteredMarketGroupsOverviewList={filteredMarketGroupsOverviewList}
                showMarketSelectorHeader={showMarketSelectorHeader}
            />
        </>
    );
};

export default MatchOfTheDayMatchListSelector;
